import { Box, CssBaseline, ThemeProvider, Toolbar } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import * as Sentry from "@sentry/react";
import React, { useCallback, useRef, useState } from "react";
import { NavigateOptions, Route, Routes, To, useLocation, useNavigate } from "react-router-dom";
import { Nullable } from "sonobello.utilities.react";
import { UserEventContextProvider } from "sonobello.utilities.react.mui/dist/components/UserEventContext/UserEventContext";

import Theme from "./assets/Theme";
import Header from "./components/Header";
import MainBackgroundContainer from "./components/main/Main.BackgroundContainer";
import DisqualifyingReasons from "./components/main/Main.DisqualifyingReasons";
import MainResizingContainer from "./components/main/Main.ResizingContainer";
import EndedSessionView, { EndedSessionViewProps } from "./components/views/EndedSessionView";
import ExpiredReservationView, { IExpiredReservationViewProps } from "./components/views/ExpiredReservationView";
import ForbiddenSessionView, { ForbiddenSessionViewProps } from "./components/views/ForbiddenSessionView";
import InvalidLinkView from "./components/views/InvalidLinkView";
import NoAvailabilityView, { NoAvailabilityViewProps } from "./components/views/NoAvailabilityView";
import NonCandidateView from "./components/views/NonCandidateView";
import SomethingWentWrongView from "./components/views/SomethingWentWrongView";
import { Step, StepPathMap } from "./types/Step";
import { CachedViewProps, ViewConnector } from "./types/ViewConnector";
import { ViewKeyMap } from "./types/Views";
import { clearLocalStorage } from "./utils/LocalStorage";
import GlobalStylesViewController from "./V2/App/Components/GlobalStylesViewController";
import { HideUserWayStyle } from "./V2/App/Components/HideUserWayStyle";
import LoadingExpander from "./V2/App/Components/LoadingExpander";
import VersionWatermark from "./V2/App/Components/VersionWatermark";
import AppContextAuthorizationRenewer from "./V2/App/Compositions/AppContextAuthorizationRenewer";
import AppContextCachedDeviceInfoReporter from "./V2/App/Compositions/AppContextCachedDeviceInfoReporter";
import AppContextReportStepProgress from "./V2/App/Compositions/AppContextReportStepProgress";
import AppContextStepProvider from "./V2/App/Compositions/AppContextStepProvider";
import AppContextUseContactPatientQualification from "./V2/App/Compositions/AppContextUseContactPatientQualification";
import AppErrorBoundary from "./V2/App/Compositions/AppErrorBoundary";
import CalendarManager from "./V2/Calendar/Compositions/CalendarManager";
import ComposedUserEventsHub from "./V2/Events/Consumers/Compositions/ComposedUserEventsHub";
import ComposedLocationUserEventCreator from "./V2/Events/Creators/Compositions/ComposedLocationUserEventCreator";
import { EventContextProvider } from "./V2/Events/EventContext";
import ExperimentContextAppContextProvider from "./V2/Experiment/Compositions/ExperimentContextAppContextProvider";
import ObxExperimentContextLoader from "./V2/Experiment/Compositions/ObxExperimentContextLoader";
import ComposedPatientNoteUpdater from "./V2/Notes/Compositions/ComposedRecordPatientNoteUpdater";
import SessionResolver from "./V2/Routing/Components/SessionResolver";
import AppContextAppRootRedirect from "./V2/Routing/Composition/AppContextAppRootRedirect";
import AppContextRedirectToFirstStep from "./V2/Routing/Composition/AppContextRedirectToFirstStep";
import ObxRoutes from "./V2/Routing/Types/AppPaths";
import AppContextCenterSelectStep from "./V2/Steps/CenterSelect/Compositions/AppContextCenterSelectStep";
import AppContextCenterSelectStepWrapper from "./V2/Steps/CenterSelect/Compositions/AppContextCenterSelectStepWrapper";
import CenterServiceModalSelector from "./V2/Steps/CenterSelect/Compositions/CenterServiceModalSelector";
import UserEventCenterSelector from "./V2/Steps/CenterSelect/Compositions/UserEventCenterSelector";
import { IStepWrapperButtonProps } from "./V2/Steps/Components/StepWrapper";
import UserEventNextButtonDesktop from "./V2/Steps/Compositions/UserEventNextButtonDesktop";
import UserEventNextButtonMobile from "./V2/Steps/Compositions/UserEventNextButtonMobile";
import ComposedDiabeticMedicalStep from "./V2/Steps/Medical/Compositions/ComposedDiabeticMedicalStep";
import ComposedGeneralMedicalStep from "./V2/Steps/Medical/Compositions/ComposedGeneralMedicalStep";
import ComposedPacemakerMedicalStep from "./V2/Steps/Medical/Compositions/ComposedPacemakerMedicalStep";
import ComposedRadiationMedicalStep from "./V2/Steps/Medical/Compositions/ComposedRadiationMedicalStep";
import AppContextQualifyStep from "./V2/Steps/Qualify/Compositions/AppContextQualifyStep";
import AppContextSchedulingLoader from "./V2/Steps/Scheduling/Compositions/AppContextSchedulingLoader";
import HybridCalendarSchedulingStep from "./V2/Steps/Scheduling/Compositions/AppContextSchedulingStepWrapper";
import TelemetryClient from "./V2/Telemetry/Compositions/TelemetryClient";
import ISession from "./V2/Types/ISession";
import ObxLoadSessionView from "./V2/Views/LoadSession/Compositions/ObxLoadSessionView";
import { IThankYouViewProps } from "./V2/Views/ThankYou/Components/ThankYouView";
import CachedThankYouView from "./V2/Views/ThankYou/Compositions/CachedThankYouView";

/** A collection of the flows supported by this application. */

const defaultNextButtonConfiguration: IStepWrapperButtonProps = {
  NextButtonDesktop: UserEventNextButtonDesktop,
  NextButtonMobile: UserEventNextButtonMobile
};

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const App: React.FC = () => {
  const navigate = useNavigate();
  const navigateWithTelemetry = useCallback(
    (to: To, navOptions?: NavigateOptions) => TelemetryClient.traceNavigation(navigate, to, navOptions),
    [navigate]
  );

  const routerLocation = useLocation();
  const [sessionState, setSessionState] = useState<Nullable<ISession>>(null);
  const [viewPropsState, setViewPropsState] = useState<unknown | undefined>(CachedViewProps.loadFromCache());
  const clearSession = useCallback(() => {
    setSessionState(null);
    clearLocalStorage();
  }, []);
  const viewConnector = useRef(
    new ViewConnector(
      clearSession,
      navigateWithTelemetry,
      sessionKey => window.location.replace(`${window.location.origin}/${sessionKey}`),
      setViewPropsState
    )
  );
  const startSession = useCallback((session: ISession) => {
    setSessionState(session);
    navigateWithTelemetry(ObxRoutes.stepPrefix);
  }, []);

  return (
    <ThemeProvider theme={Theme}>
      <GlobalStylesViewController mobileStyles={[HideUserWayStyle]} desktopStyles={[]} />
      <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={"en-US"}>
        <CssBaseline />
        <UserEventContextProvider>
          <Box height="100%" width="100%" display="flex" flexDirection="column">
            <Header pathName={routerLocation.pathname} />
            <Toolbar />
            <AppErrorBoundary
              contactId={sessionState?.contactId}
              flowId={sessionState?.flowId}
              bearerToken={sessionState?.token.token}
            >
              <Box component="main" display="flex" flexGrow={1}>
                <MainBackgroundContainer>
                  <MainResizingContainer>
                    <EventContextProvider>
                      <ComposedLocationUserEventCreator eventData={routerLocation.pathname} />
                      <ComposedUserEventsHub />
                      <SessionResolver
                        pathName={routerLocation.pathname}
                        router={viewConnector.current}
                        value={sessionState}
                        onChange={setSessionState}
                      >
                        <SentryRoutes>
                          <Route
                            path={ObxRoutes.stepPrefix}
                            element={
                              !sessionState ? (
                                <LoadingExpander />
                              ) : (
                                <ObxExperimentContextLoader
                                  experimentIdentifier={sessionState.patient.id}
                                  bearerAuthToken={sessionState.token.token}
                                >
                                  <ExperimentContextAppContextProvider
                                    bearerAuthToken={sessionState.token.token}
                                    session={sessionState}
                                    pathName={routerLocation.pathname}
                                    viewConnector={viewConnector.current}
                                  >
                                    <AppContextAppRootRedirect pathName={routerLocation.pathname}>
                                      <AppContextStepProvider
                                        pathName={routerLocation.pathname}
                                        router={viewConnector.current}
                                      />
                                      <DisqualifyingReasons viewConnector={viewConnector.current} />
                                      <ComposedPatientNoteUpdater />
                                      <AppContextCachedDeviceInfoReporter />
                                      <CalendarManager
                                        router={viewConnector.current}
                                        pathName={routerLocation.pathname}
                                      />
                                      <AppContextReportStepProgress pathName={routerLocation.pathname} />
                                      <AppContextAuthorizationRenewer router={viewConnector.current} />
                                      <AppContextUseContactPatientQualification />
                                    </AppContextAppRootRedirect>
                                  </ExperimentContextAppContextProvider>
                                </ObxExperimentContextLoader>
                              )
                            }
                          >
                            <Route
                              path={StepPathMap[Step.qualify]}
                              element={<AppContextQualifyStep {...defaultNextButtonConfiguration} />}
                            />
                            <Route
                              path={StepPathMap[Step.medicalGeneral]}
                              element={<ComposedGeneralMedicalStep {...defaultNextButtonConfiguration} />}
                            />
                            <Route
                              path={StepPathMap[Step.medicalPacemaker]}
                              element={<ComposedPacemakerMedicalStep {...defaultNextButtonConfiguration} />}
                            />
                            <Route
                              path={StepPathMap[Step.medicalRadiation]}
                              element={<ComposedRadiationMedicalStep {...defaultNextButtonConfiguration} />}
                            />
                            <Route
                              path={StepPathMap[Step.medicalDiabetic]}
                              element={<ComposedDiabeticMedicalStep {...defaultNextButtonConfiguration} />}
                            />
                            <Route
                              path={StepPathMap[Step.centerSelection]}
                              element={
                                <AppContextSchedulingLoader>
                                  <AppContextCenterSelectStepWrapper
                                    {...defaultNextButtonConfiguration}
                                    CenterSelectStep={props => (
                                      <AppContextCenterSelectStep {...props} CenterSelector={UserEventCenterSelector} />
                                    )}
                                    ServiceSelector={CenterServiceModalSelector}
                                  />
                                </AppContextSchedulingLoader>
                              }
                            />
                            <Route
                              path={StepPathMap[Step.scheduling]}
                              element={
                                <AppContextSchedulingLoader>
                                  <HybridCalendarSchedulingStep {...defaultNextButtonConfiguration} />
                                </AppContextSchedulingLoader>
                              }
                            />
                            <Route path="*" element={<AppContextRedirectToFirstStep />} />
                          </Route>
                          <Route
                            path={ViewKeyMap.thankYou.uri}
                            element={<CachedThankYouView viewProps={viewPropsState as IThankYouViewProps} />}
                          />
                          <Route
                            path={ViewKeyMap.completed.uri}
                            element={<ForbiddenSessionView {...(viewPropsState as ForbiddenSessionViewProps)} />}
                          />
                          <Route
                            path={ViewKeyMap.endedSession.uri}
                            element={<EndedSessionView {...(viewPropsState as EndedSessionViewProps)} />}
                          />
                          <Route
                            path={ViewKeyMap.noAvailability.uri}
                            element={<NoAvailabilityView {...(viewPropsState as NoAvailabilityViewProps)} />}
                          />
                          <Route path={ViewKeyMap.nonCandidate.uri} element={<NonCandidateView />} />
                          <Route
                            path={ViewKeyMap.expiredReservation.uri}
                            element={<ExpiredReservationView {...(viewPropsState as IExpiredReservationViewProps)} />}
                          />
                          <Route path={ViewKeyMap.invalidLink.uri} element={<InvalidLinkView />} />
                          <Route path={ViewKeyMap.somethingWentWrong.uri} element={<SomethingWentWrongView />} />
                          <Route
                            path="*"
                            element={
                              <ObxLoadSessionView
                                onChange={startSession}
                                router={viewConnector.current}
                                sessionKey={location.pathname.substring(1)}
                              />
                            }
                          />
                        </SentryRoutes>
                      </SessionResolver>
                    </EventContextProvider>
                  </MainResizingContainer>
                </MainBackgroundContainer>
              </Box>
            </AppErrorBoundary>
          </Box>
          <VersionWatermark />
        </UserEventContextProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;
